import React from 'react'
import { useTheme, Typography, Button, Box, Chip, Alert } from '@mui/material'
import { FactCheck, Schedule, Warning } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

import { FlexDiv } from '../FlexDiv'
import { AppRoutes } from '../../helpers/Routes'
import { getShortFormDate } from '../../helpers/stringHelpers'
import { PropertyInspectionUpcoming } from '../../types/inspection'

interface UpcomingInspectionCardProps {
    upcoming: PropertyInspectionUpcoming[]
}

const isOverdue = (endDate: string) => new Date(endDate) < new Date()

const formatDateRange = (startDate: string, endDate: string) => {
    return `${getShortFormDate(startDate)} – ${getShortFormDate(endDate)}`
}

export const UpcomingInspectionCard: React.FC<UpcomingInspectionCardProps> = ({
    upcoming,
}) => {
    const theme = useTheme()
    const navigate = useNavigate()

    if (upcoming.length === 0) {
        return null
    }

    const first = upcoming[0]
    const overdue = isOverdue(first.end_date)
    const moreCount = upcoming.length - 1

    return (
        <Box
            sx={{
                background: 'linear-gradient(135deg, #2a2f35 0%, #1e2328 100%)',
                borderRadius: 3,
                padding: 3,
                marginBottom: 3,
                border: `1px solid ${theme.palette.grey[700]}`,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: overdue
                        ? `linear-gradient(90deg, ${theme.palette.error.main} 0%, ${theme.palette.warning.main} 100%)`
                        : `linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.primary.main} 100%)`,
                }}
            />

            <FlexDiv
                style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: theme.spacing(2),
                }}
            >
                <FlexDiv style={{ alignItems: 'center' }}>
                    <FactCheck
                        style={{
                            color: overdue
                                ? theme.palette.error.main
                                : theme.palette.warning.main,
                            marginRight: theme.spacing(1),
                            fontSize: 24,
                        }}
                    />
                    <Typography
                        variant="h6"
                        style={{ color: '#fff', fontWeight: 600 }}
                    >
                        Upcoming Property Inspection
                    </Typography>
                </FlexDiv>
                <Chip
                    icon={overdue ? <Warning /> : <Schedule />}
                    label={overdue ? 'Overdue' : 'Upcoming'}
                    size="small"
                    color={overdue ? 'error' : 'warning'}
                    style={{ fontWeight: 600 }}
                />
            </FlexDiv>

            <Alert
                severity={overdue ? 'error' : 'warning'}
                style={{
                    marginBottom: theme.spacing(2),
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 2,
                }}
            >
                <Typography variant="body2" style={{ color: '#fff', fontWeight: 500 }}>
                    {first.name}
                    {moreCount > 0 && ` (+${moreCount} more)`}
                </Typography>
                <Typography
                    variant="caption"
                    style={{ color: '#8b949e', display: 'block', marginTop: 4 }}
                >
                    {first.inspection_type_name} ·{' '}
                    {formatDateRange(first.start_date, first.end_date)}
                </Typography>
            </Alert>

            <Button
                variant="contained"
                fullWidth
                onClick={() => navigate(AppRoutes.inspections)}
                startIcon={<FactCheck />}
                sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    padding: theme.spacing(1.5, 2),
                    borderRadius: 2,
                    '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                    },
                }}
            >
                View in Inspections
            </Button>
        </Box>
    )
}
